import { useEffect, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { useUpdatePageTitle } from "@humansignal/core";
import { activeHostMode, isChromelessPath } from "../utils/hostMode";
import { Menubar } from "../components/Menubar/Menubar";
import { AppFooter } from "../components/AppFooter/AppFooter";
import { ProjectRoutes } from "../routes/ProjectRoutes";
import { useOrgValidation } from "../hooks/useOrgValidation";
import { useEnsureActiveOrg } from "../hooks/useEnsureActiveOrg";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { OrglessHome } from "../pages/Home/OrglessHome";
import { Loading } from "../components/Loading/Loading";
import Login from "../pages/Auth/Login";
import Continue from "../pages/Auth/Continue";
import Signup from "../pages/Auth/Signup";
import VerifyEmailRequired from "../pages/Auth/VerifyEmailRequired";
import VerifyDone from "../pages/Auth/VerifyDone";
import ForgotPassword from "../pages/Auth/ForgotPassword";
import VerifyReceipt from "../pages/Auth/VerifyReceipt";
import ResetPassword from "../pages/Auth/ResetPassword";

/**
 * Auth pages render WITHOUT the Menubar — they're full-screen
 * standalone surfaces that the Capacitor mobile bundle uses for
 * first-launch sign-in. Web users still hit the Django HTML
 * templates at /user/login/ and /user/signup/; the React routes
 * here are the bundle's offline-capable equivalent.
 */
const ShellWithMenubar = ({ content }) => {
  useOrgValidation();
  // Ensure the user always lands with an active organization
  // selected. The hook will switch to the first available org if
  // ``user.active_organization`` is null; ``fetchOk`` flips true
  // ONLY after a successful round trip so the no-org empty state
  // can't flash up while the cloud is unreachable. See
  // useEnsureActiveOrg for the full contract.
  const { orgs, fetchOk } = useEnsureActiveOrg();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const pinned = localStorage.getItem("sidebar-pinned") === "true";
  const opened = pinned && localStorage.getItem("sidebar-opened") === "true";

  // Orgless user branch. Instead of the previous dead-end NoOrgState,
  // the shell renders the Discover feed as their home + surfaces the
  // /projects/<id> routes for anything they've joined. NoOrgState now
  // only appears as a soft fallback INSIDE the discover surface when
  // the platform has zero public projects at all. See
  // native/CROWDSOURCE_ARCHITECTURE.md for the full design.
  //
  // Platform staff/superusers are NOT sent to the orgless branch —
  // they can legitimately belong to no organisation while administering
  // the platform, and the Platform / Org admin pages would be
  // unreachable behind the discover-only shell.
  const isPlatformAdmin = !!(user && (user.is_staff || user.is_superuser));
  const isOrgless = !!user?.id && fetchOk && orgs.length === 0 && !isPlatformAdmin;
  // Until the organisations are known, a person with none set would be
  // taken for a member and sent to the Projects page (which shows them
  // Discover), then flipped to Home once the answer lands: two front
  // doors for one person. So the front door waits for the answer, for a
  // few seconds at most (offline, the old behaviour is better than none).
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setWaited(true), 4000);
    return () => clearTimeout(timer);
  }, []);
  const deciding = !!user?.id && !fetchOk && !isPlatformAdmin && !user?.active_organization && !waited;
  const atFrontDoor = pathname === "/" || /^\/projects\/?$/.test(pathname);

  return (
    <Menubar
      enabled={!isChromelessPath(pathname)}
      defaultOpened={opened}
      defaultPinned={pinned}
      onSidebarToggle={(visible) => localStorage.setItem("sidebar-opened", visible)}
      onSidebarPin={(pinned) => localStorage.setItem("sidebar-pinned", pinned)}
    >
      {deciding && atFrontDoor ? (
        <Loading fill />
      ) : isOrgless ? (
        <OrglessRoutes content={content} />
      ) : (
        <ProjectRoutes content={content} />
      )}
      <AppFooter />
    </Menubar>
  );
};

/**
 * Route table for orgless users. Bare "/" lands on a real home
 * (``OrglessHome``) that explains they're not in an org yet and points
 * them to Discover — we no longer hard-redirect "/" to /discover, which
 * hid that state. Everything else resolves through the normal route map
 * so an orgless user can still reach their own account — profile,
 * consents, account settings, notifications, joined projects, etc. The
 * org-scoped pages (Settings, Platform, Organization) simply render their
 * own empty / no-access state for a user with no ``active_organization``.
 */
/**
 * Shell for an extension host mode (see utils/hostMode.js): no sidebar,
 * the mode's page titles and root redirect, header hidden on the mode's
 * chromeless paths. Anonymous visitors are allowed when the mode says so.
 */
const HostModeShell = ({ content, mode }) => {
  const { pathname } = useLocation();
  // The mode may name the product in the tab title; otherwise the
  // platform's base title from APP_SETTINGS is appended as usual.
  const pageTitle = mode.pageTitle ? mode.pageTitle(pathname) : undefined;
  const ownTitle = mode.baseTitle ? (pageTitle ? `${pageTitle} | ${mode.baseTitle}` : mode.baseTitle) : pageTitle;
  useUpdatePageTitle(ownTitle, !mode.baseTitle);
  if (pathname === "/" && mode.rootRedirect) return <Redirect to={mode.rootRedirect} />;
  return (
    <Menubar enabled={!isChromelessPath(pathname)} sidebar={false}>
      <ProjectRoutes content={content} />
      {/* A host mode with its own footer (and, on a phone, its own
          bottom navigation) suppresses the platform one, which would
          otherwise reserve a band above it. */}
      {mode.appFooter !== false && <AppFooter />}
    </Menubar>
  );
};

const OrglessRoutes = ({ content }) => (
  <Switch>
    <Route path="/" exact render={() => <OrglessHome />} />
    {/* A full page load of "/" is sent to /projects by the server; a
        person with no organisation has no projects page of their own,
        so that is Home too. Discover stays at /discover. */}
    <Route path="/projects" exact render={() => <OrglessHome />} />
    <Route render={() => <ProjectRoutes content={content} />} />
  </Switch>
);

export const RootPage = ({ content }) => {
  return (
    <Switch>
      <Route path="/auth/login" exact component={Login} />
      <Route path="/auth/continue" exact component={Continue} />
      <Route path="/auth/signup" exact component={Signup} />
      <Route path="/auth/verify-email-required" exact component={VerifyEmailRequired} />
      <Route path="/auth/verify-done" exact component={VerifyDone} />
      <Route path="/auth/forgot-password" exact component={ForgotPassword} />
      {/* Public receipt verification — anonymous by design (see
          VerifyReceipt). Top-level, outside the shell: a standalone
          route above the catch-all, like the auth pages. */}
      <Route path="/verify-receipt" exact component={VerifyReceipt} />
      <Route path="/auth/reset-password/:uidb64/:token" exact component={ResetPassword} />
      <Route
        path="/"
        render={() =>
          activeHostMode ? (
            <HostModeShell content={content} mode={activeHostMode} />
          ) : (
            <ShellWithMenubar content={content} />
          )
        }
      />
    </Switch>
  );
};
